import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { PageContainer } from '../components/layout/page-container';
import { PageHeader } from '../components/ui/page-header';
import { getSupabase } from '../lib/supabase';

/**
 * 비밀번호 재설정.
 *
 * **왜 이 화면이 필요한가:** Supabase 의 재설정 메일은 Site URL(`zoxo.kr`)로
 * 돌아오면서 토큰을 **URL 해시**에 실어 준다
 * (`#access_token=…&type=recovery`). 그런데 이 앱은 `detectSessionInUrl: false`
 * 라 SDK 가 해시를 자동으로 소비하지 않고, `/auth/callback` 은 카카오 OAuth
 * 전용(`?code=`)이라 해시를 못 본다. 그래서 링크를 눌러도 그냥 첫 화면이 뜨고
 * 끝났다 — 토큰이 URL 에 남은 채 아무도 읽지 않았다.
 *
 * 여기서 해시의 토큰으로 세션을 세우고 새 비밀번호를 받는다.
 */
export const Route = createFileRoute('/auth/reset')({
  // 로그인 화면 — 세션이 브라우저에만 있다. 서버는 레이아웃만 그린다(docs/SSR.md).
  ssr: false,
  component: ResetPassword,
});

/** Supabase 가 돌려주는 해시는 쿼리스트링과 같은 모양이다 (`#a=1&b=2`). */
function readHash(): { accessToken?: string; refreshToken?: string; type?: string; error?: string } {
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return {
    accessToken: p.get('access_token') ?? undefined,
    refreshToken: p.get('refresh_token') ?? undefined,
    type: p.get('type') ?? undefined,
    // 만료된 링크는 토큰 대신 error_description 을 싣고 온다.
    error: p.get('error_description') ?? p.get('error') ?? undefined,
  };
}

type Phase = 'checking' | 'ready' | 'saving' | 'done' | 'invalid';

function ResetPassword() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    const { accessToken, refreshToken, error } = readHash();

    if (error) {
      setPhase('invalid');
      setMessage('링크가 만료됐거나 이미 사용됐어요. 재설정 메일을 다시 받아 주세요.');
      return;
    }
    if (!accessToken || !refreshToken) {
      // 이미 로그인한 상태로 직접 들어온 경우는 그대로 진행할 수 있다.
      void getSupabase().auth.getSession().then(({ data }) => {
        if (data.session) setPhase('ready');
        else {
          setPhase('invalid');
          setMessage('재설정 링크로 접속해 주세요.');
        }
      });
      return;
    }

    void getSupabase().auth
      .setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(({ error: sessionError }) => {
        if (sessionError) {
          setPhase('invalid');
          setMessage('링크가 만료됐거나 이미 사용됐어요. 재설정 메일을 다시 받아 주세요.');
          return;
        }
        // 토큰을 주소창에서 지운다. 새로고침·공유로 새어 나가지 않게.
        window.history.replaceState(null, '', window.location.pathname);
        setPhase('ready');
      });
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setMessage('비밀번호는 8자 이상이어야 해요.');
      return;
    }
    if (password !== confirm) {
      setMessage('두 입력이 서로 달라요.');
      return;
    }
    setPhase('saving');
    setMessage(null);

    const { error } = await getSupabase().auth.updateUser({ password });
    if (error) {
      setPhase('ready');
      setMessage(error.message);
      return;
    }
    setPhase('done');
  };

  return (
    <PageContainer>
      <PageHeader title="비밀번호 재설정" />

      {phase === 'checking' && <p className="text-sm text-text-muted">확인 중…</p>}

      {phase === 'invalid' && (
        <div className="space-y-3">
          <p className="text-sm text-danger">{message}</p>
          <Button type="button" variant="secondary" onClick={() => void navigate({ to: '/' })}>
            홈으로
          </Button>
        </div>
      )}

      {phase === 'done' && (
        <div className="space-y-3">
          <p className="text-sm text-text">비밀번호를 바꿨어요. 새 비밀번호로 로그인해 주세요.</p>
          <Button type="button" onClick={() => void navigate({ to: '/' })}>
            홈으로
          </Button>
        </div>
      )}

      {(phase === 'ready' || phase === 'saving') && (
        <form className="max-w-sm space-y-3" onSubmit={(e) => void submit(e)} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="pw" className="text-sm text-text-muted">
              새 비밀번호
            </label>
            <input
              id="pw"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="pw2" className="text-sm text-text-muted">
              한 번 더
            </label>
            <input
              id="pw2"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {message && (
            <p className="text-xs text-danger" role="alert">
              {message}
            </p>
          )}

          <Button type="submit" disabled={phase === 'saving'}>
            {phase === 'saving' ? '저장 중…' : '비밀번호 변경'}
          </Button>
        </form>
      )}
    </PageContainer>
  );
}
